import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import './Layout.css';

function Layout({ children, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = async () => {
    if (loggingOut) return; // Prevent multiple clicks
    setLoggingOut(true);
    try {
      await onLogout();
    } catch (error) {
      console.error('Logout error:', error);
      setLoggingOut(false);
    }
  };

  const menuItems = [
    { path: '/', icon: '🏠', label: 'Home', key: 'home' },
    { path: '/admin/dashboard', icon: '📊', label: 'Dashboard', key: 'dashboard' },
    { path: '/admin/all-submissions', icon: '📋', label: 'All Submissions', key: 'all-submissions' },
    { path: '/admin/workers', icon: '👷', label: 'Workers', key: 'workers' },
    { path: '/admin/manage-documents', icon: '📄', label: 'Manage Documents', key: 'manage-documents' },
    { path: '/admin/database', icon: '🗄️', label: 'Database Backup', key: 'database' },
    { path: '/admin/change-password', icon: '🔐', label: 'Change Password', key: 'change-password' },
    { path: '/admin/email-settings', icon: '📧', label: 'Email Settings', key: 'email-settings' }
  ];

  const isMobile = () => window.innerWidth <= 768;

  const handleToggle = () => {
    const mobile = isMobile();
    console.log('Toggle clicked - isMobile:', mobile, 'window.innerWidth:', window.innerWidth);
    if (mobile) {
      console.log('Setting mobileMenuOpen to:', !mobileMenuOpen);
      setMobileMenuOpen(!mobileMenuOpen);
    } else {
      console.log('Setting sidebarCollapsed to:', !sidebarCollapsed);
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const handleNavigation = (path) => {
    // Navigate to the path directly
    navigate(path);

    // Close mobile menu after navigation
    if (isMobile()) {
      console.log('Closing mobile menu after navigation');
      setMobileMenuOpen(false);
    }
  };

  // Debug: log state changes
  console.log('Layout render - mobileMenuOpen:', mobileMenuOpen, 'sidebarCollapsed:', sidebarCollapsed);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.hash === '#/' || location.hash === '';
    }
    if (path === '/admin/dashboard') {
      return location.pathname === '/admin/dashboard' || location.hash === '#/admin/dashboard' ||
             location.pathname === '/dashboard' || location.hash === '#/dashboard' ||
             location.pathname === '/admin' || location.hash === '#/admin';
    }
    if (path === '/admin/all-submissions') {
      return location.pathname === '/admin/all-submissions' || location.hash === '#/admin/all-submissions';
    }
    if (path === '/admin/workers') {
      return location.pathname === '/admin/workers' || location.hash === '#/admin/workers' ||
             location.pathname === '/workers' || location.hash === '#/workers';
    }
    if (path === '/admin/manage-documents') {
      return location.pathname === '/admin/manage-documents' || location.hash === '#/admin/manage-documents';
    }
    if (path === '/admin/database') {
      return location.pathname === '/admin/database' || location.hash === '#/admin/database';
    }
    if (path === '/admin/email-settings') {
      return location.pathname === '/admin/email-settings' || location.hash === '#/admin/email-settings';
    }
    // For other paths, strip /admin prefix and check both versions
    const strippedPath = path.replace('/admin', '');
    return location.pathname === path || location.hash === `#${path}` ||
           location.pathname === strippedPath || location.hash === `#${strippedPath}`;
  };

  return (
    <div className="modern-layout">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-label">WORKPLACE</div>
            {menuItems.map((item) => (
              <button
                key={item.key}
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => handleNavigation(item.path)}
              >
                <span className="nav-icon">{item.icon}</span>
                {!sidebarCollapsed && <span className="nav-text">{item.label}</span>}
                {!sidebarCollapsed && isActive(item.path) && <span className="nav-arrow">›</span>}
              </button>
            ))}
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="user-profile">
            <div className="user-avatar">👤</div>
            {!sidebarCollapsed && (
              <div className="user-info">
                <div className="user-name">{user?.username || 'Admin'}</div>
              </div>
            )}
          </div>
          {!sidebarCollapsed && (
            <button className="help-btn" onClick={() => navigate('/help')}>
              <span>❓</span> Help
              <span className="arrow">›</span>
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button
              className="sidebar-toggle"
              onClick={handleToggle}
              title="Toggle sidebar"
            >
              ☰
            </button>
            <div className="header-brand">
              <img src="/logo-small.png" alt="SSM" className="header-logo" />
            </div>
          </div>

          <div className="header-actions">
            <NotificationBell />
            <button
              className={`logout-btn ${loggingOut ? 'logging-out' : ''}`}
              onClick={handleLogout}
              disabled={loggingOut}
              title="Logout"
            >
              {loggingOut ? (
                <>
                  <span className="logout-spinner"></span>
                  <span>Logging out...</span>
                </>
              ) : (
                'Logout'
              )}
            </button>
          </div>
        </header>

        <main className="content-area">
          {children}
        </main>
      </div>
    </div>
  );
}

export default Layout;
